import {
  type MapIslandDto,
  type StoryListItemDto,
  type MissionDto,
  type PlayerRaidDto,
} from '@/api/hooks'
import { RouteLayer, type RouteFleet } from '@/components/game/map/RouteLayer'
import { MISSION_COLOR, RECIT_COLOR } from '@/components/game/map/mapMarkers'
import { isReturningMission } from '@/lib/inProgress'

/**
 * SVG overlay of every outbound route on the map: in-progress missions (incl. NPC
 * raids, which are missions) AND in-flight PvP raids (a separate `PlayerRaid`
 * entity, so they need their own pass — that omission is why a launched PvP raid
 * showed no line). Adapter: maps the DTOs to {@link RouteFleet}[] and delegates all
 * rendering (lines, fleet markers, focus/hover) to {@link RouteLayer}; PvP raids
 * resolve their destination from `defenderIslandId` and use the `raid_pvp` colour.
 *
 * Exported so it can be mounted in isolation under tests (no sea backdrop, just
 * the routes). The viewport props (`k`, `selectedIslandId`, `hoveredKey`, `onHover`)
 * default to a static, un-zoomed layer so callers that don't drive a viewport
 * (e.g. tests) keep working unchanged. `MapViewport` is the live caller, passing
 * the current zoom + focus state.
 */
export function MapRoutes({
  islands,
  gridSize,
  myIsland,
  missions,
  pvpRaids,
  recits = [],
  k = 1,
  selectedIslandId = null,
  hoveredKey = null,
  onHover,
  onSelectFleet,
}: {
  islands: MapIslandDto[]
  gridSize: number
  myIsland: MapIslandDto
  /**
   * Already filtered to missions with a destination island that are either
   * in_progress (outbound) or returning (cancelled, not yet landed —
   * {@link isReturningMission}).
   */
  missions: MissionDto[]
  /** This captain's SENT raids; filtered to traveling here. */
  pvpRaids: PlayerRaidDto[]
  /** Récits (expeditions) in flight — already filtered to traveling/returning with a target island. */
  recits?: StoryListItemDto[]
  k?: number
  selectedIslandId?: number | null
  hoveredKey?: string | null
  onHover?: (key: string | null) => void
  onSelectFleet?: (key: string) => void
}) {
  const fleets: RouteFleet[] = []

  for (const m of missions) {
    // ⚠️ Une chasse légendaire n'a PAS d'île de destination : sans ce cas, la
    // flotte partait sans qu'aucun trait ne la suive (le `islands.find` la
    // jetait silencieusement). La cible se lit alors dans le payload de mission,
    // où le service de lancement a figé les coordonnées de la bête.
    if (m.type === 'kraken_hunt') {
      const tx = m.payload?.targetX
      const ty = m.payload?.targetY
      if (typeof tx !== 'number' || typeof ty !== 'number') continue
      fleets.push({
        key: `m-${m.id}`,
        destinationIslandId: null,
        origin: { x: myIsland.x, y: myIsland.y },
        destination: { x: tx, y: ty },
        color: MISSION_COLOR.kraken_hunt ?? '#d9584a',
        count: m.shipCount,
        label: m.typeLabel,
        destName: typeof m.payload?.archetypeLabel === 'string' ? m.payload.archetypeLabel : 'la bête',
        startedAt: m.startedAt,
        resolvesAt: m.resolvesAt,
      })
      continue
    }

    // Même cas que la chasse : la carrière n'est pas une île, ses coordonnées sont
    // figées dans le payload au lancement du convoi.
    if (m.type === 'quarry_convoy') {
      const tx = m.payload?.targetX
      const ty = m.payload?.targetY
      if (typeof tx !== 'number' || typeof ty !== 'number') continue
      fleets.push({
        key: `m-${m.id}`,
        destinationIslandId: null,
        origin: { x: myIsland.x, y: myIsland.y },
        destination: { x: tx, y: ty },
        color: MISSION_COLOR.quarry_convoy ?? '#8FD1DF',
        count: m.shipCount,
        label: m.typeLabel,
        destName: 'la carrière',
        startedAt: m.startedAt,
        resolvesAt: m.resolvesAt,
      })
      continue
    }

    const dest = islands.find((i) => i.id === m.destinationIslandId)
    if (!dest) continue
    const home = { x: myIsland.x, y: myIsland.y }
    const isle = { x: dest.x, y: dest.y }

    if (isReturningMission(m)) {
      // Cancelled, still at sea: the fleet is sailing FROM the destination BACK
      // home, not outbound — draw the line reversed so the arrow/marker don't
      // lie about direction. The backend rewrites `resolvesAt` to the return
      // arrival time at the moment of cancel but never records that cancel
      // instant, so the exact return-leg start isn't recoverable from the DTO;
      // we approximate it the same way the server's own cancellation-eligibility
      // window does (half the elapsed startedAt→resolvesAt span), which is exact
      // whenever the 30s floor didn't kick in (see MissionCancelService).
      const started = new Date(m.startedAt).getTime()
      const resolves = new Date(m.resolvesAt).getTime()
      const legMs = Math.max(1, (resolves - started) / 2)
      fleets.push({
        key: `m-${m.id}`,
        destinationIslandId: dest.id,
        origin: isle,
        destination: home,
        color: MISSION_COLOR[m.type] ?? '#5FBDD0',
        count: m.shipCount,
        label: `Retour · ${m.typeLabel}`,
        destName: myIsland.name,
        startedAt: new Date(resolves - legMs).toISOString(),
        resolvesAt: m.resolvesAt,
      })
      continue
    }

    fleets.push({
      key: `m-${m.id}`,
      destinationIslandId: dest.id,
      origin: home,
      destination: isle,
      color: MISSION_COLOR[m.type] ?? '#5FBDD0',
      count: m.shipCount,
      label: m.typeLabel,
      destName: dest.name,
      startedAt: m.startedAt,
      resolvesAt: m.resolvesAt,
    })
  }

  for (const r of pvpRaids) {
    if (r.status !== 'traveling' || !r.scheduledAt) continue
    const dest = islands.find((i) => i.id === r.defenderIslandId)
    if (!dest) continue
    fleets.push({
      key: `pvp-${r.id}`,
      destinationIslandId: dest.id,
      origin: { x: myIsland.x, y: myIsland.y },
      destination: { x: dest.x, y: dest.y },
      color: MISSION_COLOR.raid_pvp,
      count: r.shipCount,
      label: 'Raid',
      destName: dest.name,
      startedAt: r.createdAt,
      resolvesAt: r.scheduledAt,
    })
  }

  // Récits (expeditions): outbound while traveling, homebound while returning.
  // RouteLayer's marker does a full out-and-back over [startedAt, resolvesAt], so we
  // feed a synthetic end one leg PAST the turnaround — the marker then covers exactly
  // one leg (origin→destination) over the visible window, reaching the far end on time.
  for (const r of recits) {
    if (r.state !== 'traveling' && r.state !== 'returning') continue
    if (!r.destinationIslandId) continue
    const dest = islands.find((i) => i.id === r.destinationIslandId)
    if (!dest) continue
    const home = { x: myIsland.x, y: myIsland.y }
    const isle = { x: dest.x, y: dest.y }
    const started = new Date(r.startedAt).getTime()
    const arrives = new Date(r.arrivesAt).getTime()
    const legMs = Math.max(1, arrives - started)

    if (r.state === 'returning' && r.returnsAt) {
      const returns = new Date(r.returnsAt).getTime()
      fleets.push({
        key: `recit-${r.id}`,
        destinationIslandId: dest.id,
        origin: isle,
        destination: home,
        color: RECIT_COLOR,
        count: r.shipCount,
        label: 'Expédition',
        destName: dest.name,
        startedAt: new Date(returns - legMs).toISOString(),
        resolvesAt: new Date(returns + legMs).toISOString(),
      })
    } else {
      fleets.push({
        key: `recit-${r.id}`,
        destinationIslandId: dest.id,
        origin: home,
        destination: isle,
        color: RECIT_COLOR,
        count: r.shipCount,
        label: 'Expédition',
        destName: dest.name,
        startedAt: r.startedAt,
        resolvesAt: new Date(arrives + legMs).toISOString(),
      })
    }
  }

  return (
    <RouteLayer
      fleets={fleets}
      gridSize={gridSize}
      k={k}
      selectedIslandId={selectedIslandId}
      hoveredKey={hoveredKey}
      onHover={onHover}
      onSelectFleet={onSelectFleet}
    />
  )
}
